import { useMemo } from "react";
import type { DocumentSourcesResponse } from "../../types";
import { formatNumber } from "../../utils/formatters";
import styles from "./DocumentSourcesTable.module.css";

type SourceEntry = DocumentSourcesResponse["sources"][number];

const MAX_ROWS = 20;

export interface DocumentSourcesTableProps {
  data: DocumentSourcesResponse;
}

export function DocumentSourcesTable({ data }: DocumentSourcesTableProps) {
  const sorted = useMemo(
    () =>
      [...data.sources]
        .sort((a, b) => b.reference_count - a.reference_count)
        .slice(0, MAX_ROWS),
    [data.sources],
  );

  if (sorted.length === 0) {
    return (
      <div className={styles.emptyState}>No document source data for this period.</div>
    );
  }

  const maxCount = sorted[0]?.reference_count ?? 1;

  return (
    <div className={styles.container}>
      <table className={styles.table} role="table">
        <thead>
          <tr>
            <th className={styles.rankCol}>#</th>
            <th className={styles.docCol}>Document</th>
            <th className={styles.orgCol}>Organisation</th>
            <th className={styles.countCol}>References</th>
            <th className={styles.barCol} aria-hidden="true" />
          </tr>
        </thead>
        <tbody>
          {sorted.map((source, idx) => (
            <TableRow
              key={`${source.org_id}-${source.doc_id}`}
              source={source}
              rank={idx + 1}
              maxCount={maxCount}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface TableRowProps {
  source: SourceEntry;
  rank: number;
  maxCount: number;
}

function TableRow({ source, rank, maxCount }: TableRowProps) {
  const barWidth = maxCount > 0 ? (source.reference_count / maxCount) * 100 : 0;

  return (
    <tr className={styles.row}>
      <td className={styles.rankCell}>{rank}</td>
      <td className={styles.docCell} title={source.doc_name ?? source.doc_id}>
        {source.doc_name ?? source.doc_id}
      </td>
      <td className={styles.orgCell} title={source.org_name ?? source.org_id}>
        {source.org_name ?? source.org_id}
      </td>
      <td className={styles.countCell}>{formatNumber(source.reference_count)}</td>
      <td className={styles.barCell}>
        <div className={styles.barTrack}>
          <div className={styles.barFill} style={{ width: `${barWidth}%` }} />
        </div>
      </td>
    </tr>
  );
}
